// =============================================================================
// Aprobaciones (mig. 058, oct-2026) — lo que el cajero pide y el admin aprueba.
//
// Cuando un cajero reduce stock, desactiva un item o borra un gasto, el backend
// NO lo ejecuta: responde 202 con `pendiente_aprobacion: true`. Quien llame a
// esos endpoints tiene que mirar `esPendiente()` antes de mostrar "listo".
// =============================================================================
import { api } from './client';

export type AprobacionTipo = 'inventario_reducir' | 'inventario_desactivar' | 'gasto_borrar';
export type AprobacionEstado = 'pendiente' | 'aprobada' | 'rechazada' | 'cancelada';

export interface Aprobacion {
  id: number;
  tipo: AprobacionTipo;
  sede_id: number | null;
  sede_nombre: string | null;
  objeto_id: number;
  payload: Record<string, any>;
  resumen: string;
  estado: AprobacionEstado;
  solicitado_por: number | null;
  solicitado_por_nombre: string | null;
  solicitado_at: string;
  resuelto_por: number | null;
  resuelto_por_nombre: string | null;
  resuelto_at: string | null;
  motivo_rechazo: string | null;
  resultado: Record<string, any> | null;
  /** Lo decide el backend por fila: el front solo muestra el botón. */
  puede_resolver?: boolean;
  puede_cancelar?: boolean;
}

/** Respuesta 202 de un endpoint que convirtió la acción en solicitud. */
export interface RespuestaPendiente {
  pendiente_aprobacion: true;
  aprobacion: { id: number; tipo: AprobacionTipo; estado: AprobacionEstado; resumen: string };
  mensaje: string;
}

export function esPendiente(r: unknown): r is RespuestaPendiente {
  return !!r && typeof r === 'object' && (r as any).pendiente_aprobacion === true;
}

// Claves (no textos) de i18n/modulos/aprobaciones.ts: se traducen en el render.
export const TIPO_LABEL: Record<AprobacionTipo, 'tipoInventarioReducir' | 'tipoInventarioDesactivar' | 'tipoGastoBorrar'> = {
  inventario_reducir: 'tipoInventarioReducir',
  inventario_desactivar: 'tipoInventarioDesactivar',
  gasto_borrar: 'tipoGastoBorrar',
};

export const listAprobaciones = (opts: { estado?: AprobacionEstado; sede_id?: number } = {}) => {
  const p = new URLSearchParams();
  if (opts.estado) p.set('estado', opts.estado);
  if (opts.sede_id) p.set('sede_id', String(opts.sede_id));
  const qs = p.toString();
  return api.get<Aprobacion[]>(`/api/aprobaciones${qs ? `?${qs}` : ''}`);
};

export const contadorAprobaciones = () =>
  api.get<{ accionables: number; mias_pendientes: number }>('/api/aprobaciones/contador');

export const aprobarSolicitud = (id: number) =>
  api.post<Aprobacion>(`/api/aprobaciones/${id}/aprobar`, {});

export const rechazarSolicitud = (id: number, motivo: string) =>
  api.post<Aprobacion>(`/api/aprobaciones/${id}/rechazar`, { motivo });

export const cancelarSolicitud = (id: number) =>
  api.post<Aprobacion>(`/api/aprobaciones/${id}/cancelar`, {});
